import type { EditRequest, EditDeltaData, EditEntityKind, EditOp, ReviewMode } from './types';

export interface PendingEdit {
  id: string;
}

/** Minimal stub stored in the edit-action store during the requesting→streaming gap.
 *  Carries the kind discriminant and the target end position so the caret effect can
 *  render without waiting for the applier's begin() to resolve. */
export interface RequestingPending extends PendingEdit {
  kind: 'text' | 'canvas';
  to: number;
}

export interface EditApplier {
  readonly entityKind: EditEntityKind;
  supports(op: EditOp['op']): boolean;
  begin(req: EditRequest, mode?: ReviewMode): PendingEdit;
  applyChunk(pending: PendingEdit, data: EditDeltaData): void | Promise<void>;
  accept(pending: PendingEdit): void;
  reject(pending: PendingEdit): void;
  /** Optional: stop the streaming animation (shimmer/caret) once the stream ends
   *  and the edit enters review. Appliers without a streaming preview omit it. */
  freezePreview?(pending: PendingEdit): void;
}

export class EditApplierRegistry {
  private byKind = new Map<EditEntityKind, EditApplier>();

  register(applier: EditApplier): void {
    this.byKind.set(applier.entityKind, applier);
  }

  deregister(kind: EditEntityKind): void {
    this.byKind.delete(kind);
  }

  forKind(kind: EditEntityKind): EditApplier | undefined {
    return this.byKind.get(kind);
  }
}
